import type { OpUnitType } from "dayjs";
import dayjs from "dayjs";
import { rgba } from "@similarweb/styles";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import type { ChartType } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames, granularityNameToMomentUnit } from "app/UtilitiesAndConstants/Constants/Moment";
import type { RawDate } from "app/UtilitiesAndConstants/types/dates";

const MONTH_TO_DATE_ENDPOINT_VALUE = { latest: "l" };

export const getMonthsToDateTooltipText = (isMonthToDateAvailable: boolean, isMonthToDateActive: boolean): string => {
    return isMonthToDateAvailable
        ? isMonthToDateActive
            ? translate("wa.traffic.engagement.over.time.mtd.tooltip", {
                  date: swSettings.current.lastSupportedDailyDate.format("MMM DD"),
              })
            : translate("wa.traffic.engagement.over.time.mtd.tooltip.inactive")
        : translate("wa.traffic.engagement.over.time.mtd.tooltip.unavailable");
};

export const makeZone = (point: { x: any }, { isPartial, chartType, color }: any) => {
    let zoneStyle;
    if (isPartial) {
        switch (chartType) {
            case chartTypes.AREA:
                zoneStyle = {
                    dashStyle: "solid",
                    fillColor: {
                        pattern: {
                            path: {
                                d: "M 0 0 L 17 17 M -3 14 L 3 20 M 14 -3 L 20 3",
                                strokeWidth: 6,
                                stroke: color,
                            },
                            width: 17,
                            height: 17,
                            backgroundColor: rgba(color, 0.9),
                        },
                    },
                };
                break;
            case chartTypes.COLUMN:
                zoneStyle = {
                    dashStyle: "solid",
                    color: {
                        pattern: {
                            path: {
                                d: "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11",
                                fill: color,
                                strokeWidth: 3.5,
                                stroke: color,
                            },
                            width: 10,
                            height: 10,
                            backgroundColor: rgba(color, 0.9),
                        },
                    },
                };
                break;
            case chartTypes.LINE:
            default:
                zoneStyle = {
                    dashStyle: "dash",
                };
        }
    } else {
        zoneStyle = {
            dashStyle: "solid",
        };
    }
    return {
        value: point?.x,
        ...zoneStyle,
    };
};

export const isPartialDataPoint = (
    idx: number,
    arr: readonly any[],
    item: { Key?: any; Values?: readonly any[] },
    timeGranularityName: GranularityNames,
    lastSupportedDate: RawDate,
) => {
    const dataPointDate = dayjs.utc(item?.Key || (item.Values && item.Values[0]?.Key));
    const lastSupportedSnapshotInterval = swSettings.current.endDate;
    if (idx !== arr.length - 1) return false;
    if (timeGranularityName === GranularityNames.MONTHLY) {
        // when analyzing monthly granularity, a "partial" data point is one that after the last supported snapshot date
        return (
            dataPointDate.isAfter(lastSupportedSnapshotInterval) &&
            !dataPointDate.isSame(dataPointDate.endOf("month"), "day")
        );
    } else if (timeGranularityName === GranularityNames.WEEKLY) {
        return dataPointDate
            .add(1, granularityNameToMomentUnit[timeGranularityName] as OpUnitType)
            .subtract(1, "days")
            .isAfter(lastSupportedDate);
    }
    return false; // daily
};

export const addPartialDataZones = (graphSeries: any, { chartType }: { chartType?: ChartType } = {}) =>
    graphSeries.map((series: any) => {
        const type = chartType || series.type;
        return {
            ...series,
            zoneAxis: "x",
            zones: series.data.reduce((acc: any, point: any, idx: any) => {
                const nextPoint = series.data[idx + 1];
                const isPartial = point?.isPartial ?? point?.custom?.isPartial ?? false;
                const nextIsPartial = nextPoint?.isPartial ?? nextPoint?.custom?.isPartial ?? false;

                if (!nextPoint || isPartial !== nextIsPartial) {
                    const zonePoint = nextPoint
                        ? [chartTypes.LINE, chartTypes.AREA].includes(type)
                            ? point
                            : nextPoint
                        : undefined;

                    acc.push(makeZone(zonePoint, { chartType: type, color: series.color, isPartial: isPartial }));
                }

                return acc;
            }, []),
        };
    });

export const getMonthToDateEndPointValue = () => MONTH_TO_DATE_ENDPOINT_VALUE;

export const monthToDateTracking = () => (monthToDateValue: any) =>
    allTrackers.dispatchTrackEvent("Button", "switch", `Over Time Graph/toggle_mtd/${monthToDateValue ? "on" : "off"}`);
